import { CloseOutlined } from '@ant-design/icons';
import { useLocalStorageState } from 'ahooks';
import { Alert } from 'antd';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import RedWarmingFill from '@/assets/images/icons/red-warming-fill.react.svg?react';
import WarmingFillMax from '@/assets/images/icons/warming-fill.max.react.svg?react';
import { useAppConfig } from '@/config/context';
import { isPro } from '@/infrastructure/auth';
import { ENV } from '@/infrastructure/env-config';
import { getParamFromUrl } from '@/infrastructure/history';
import { formatNum } from '@/infrastructure/utils/digit';
import styles from '@/presentation/components/arrears-reminder/index.module.scss';
import { ResourceType } from '@/presentation/pages/resource-pack/types';
import { useStores } from '@/presentation/store';
import { ProductExceedStatus, Dimension, ResourceStatus } from '@/proto/resource-manager';
import { FreeStatus } from '@/service/pro-user';
import { ProductType } from '@/service/product-permissions';
import { resourcePackageService } from '@/service/resource-package';
import type { IResourcePackage } from '@/service/resource-package';
import { Banner } from '../banner-pro';

interface IProps {
  showArrearsBanner?: boolean
  setShowArrearsBanner?: React.Dispatch<React.SetStateAction<boolean>>;
  showOverdueDataBanner?: boolean
  setShowOverdueDataBanner?: React.Dispatch<React.SetStateAction<boolean>>;
  showBanner: boolean,
  setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
  showComplianceBanner: boolean,
  setShowComplianceBanner: React.Dispatch<React.SetStateAction<boolean>>;
  isShowComplianceModal: boolean,
  setIsShowComplianceModal: React.Dispatch<React.SetStateAction<boolean>>;
  liteModeRoutes: string[];
}

export const ArrearsReminder = observer((props: IProps) => {
  const {
    showArrearsBanner,
    setShowArrearsBanner,
    showOverdueDataBanner,
    setShowOverdueDataBanner,
    showBanner,
    setShowBanner,
    showComplianceBanner,
    setShowComplianceBanner,
    isShowComplianceModal,
    setIsShowComplianceModal,
    liteModeRoutes,
  } = props;
  const { productExceed = {}, product, productType } = useStores().ProductStore;
  const { status, exceedQuota } = productExceed;
  const { overdueDataRecoveryBanner = {} } = useStores().ProductStore;
  const { banner } = overdueDataRecoveryBanner;
  const [time, setTime] = useLocalStorageState<number | undefined>(
    'arrears-reminder-time',
    {
      defaultValue: 0,
    },
  );
  const linkParams = new URLSearchParams();
  const location = useLocation();
  const config = useAppConfig();
  const isLiteRoute = liteModeRoutes.includes(location.pathname);

  linkParams.set('appId', getParamFromUrl('productId'));

  const close = () => {
    setTime(moment().unix());
  };

  // 是否超过1天
  const isExceed1Day = useMemo(() => {
    if (!time) {
      return true;
    }
    const storageTime =  moment.unix(time);
    const currentTime = moment();
    return currentTime.diff(storageTime, 'hours') >= 24;
  }, [time]);


  useEffect(() => {
    if (
      ![ProductExceedStatus.WILL_EXCEED,
        ProductExceedStatus.HAS_ALREADY_EXCEED,
        ProductExceedStatus.WILL_EXPIRE].includes(status) || !isExceed1Day) {
      setShowArrearsBanner?.(false);
      return;
    }

    setShowArrearsBanner?.(true);
  }, [status, isExceed1Day]);
  const [processedMessage, setProcessedMessage] = useState(banner);

  // 计算是否绑定了月活套餐（DeviceUnLimited）
  const [hasMauPackage, setHasMauPackage] = useState<boolean>(false);
  useEffect(() => {
    if (isLiteRoute) return;

    // 海外版同样沿用此逻辑，仅做展示文案的区分
    resourcePackageService.getProductResourceList({
      queryParam: {
        pageParam: {
          page: 1,
          pageSize: 100,
        },
        // 需要包含当天
        timeParam: {
          startTime: moment()
            .subtract(6, 'days')
            .startOf('day')
            .unix(),
          endTime: moment().endOf('day')
            .unix(),
        },
      },
      resourceStatus: ResourceStatus.VALID,
      dim: Dimension.PRODUCT,
    }).then((res) => {
      const list: IResourcePackage[] = res?.resources || [];
      setHasMauPackage(list.some((item: IResourcePackage) => item?.resourceType === ResourceType.DeviceUnLimited));
    })
      .catch(() => {
        setHasMauPackage(false);
      });
  }, []);

  useEffect(() => {
    if (!banner) {
      setShowOverdueDataBanner?.(false);
    } else {
      setShowOverdueDataBanner?.(true);
      // 海外版没有产品计费文档，暂时不处理
      if (banner?.includes('产品计费文档') && isPro()) {
        setProcessedMessage(banner.replace(
          /(产品计费文档)/g,
          `<a href="${config.billingDocumentsLink}" style="color: #1890ff; text-decoration: none;" target="_blank">$1</a>`,
        ));
      } else {
        setProcessedMessage(banner);
      }
    }
  }, [banner]);

  if (showArrearsBanner && !liteModeRoutes.includes(location.pathname)) {
    return (
      <div
        className={classnames({
          [styles.arrearsReminder]: true,
          [styles.willExceed]: [ProductExceedStatus.WILL_EXCEED, ProductExceedStatus.WILL_EXPIRE].includes(status),
          [styles.hasAlreadyExceed]:
            status === ProductExceedStatus.HAS_ALREADY_EXCEED,
        })}
      >
        {status === ProductExceedStatus.WILL_EXCEED && !ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            尊敬的用户，当前产品资源包剩余上报量少于{hasMauPackage ? '最近两个月' : '最近15天'}用量，请及时
            <Link
              to={{ pathname: '/resource-pack', search: linkParams.toString() }}
              target="_blank"
            >
              购买资源包
            </Link>
            ，以免影响数据上报、告警通知等服务。
          </span>
        ) : null}
        {status === ProductExceedStatus.WILL_EXPIRE && !ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            尊敬的用户，当前产品套餐即将过期，请及时
            <Link
              to={{ pathname: '/resource-pack', search: linkParams.toString() }}
              target="_blank"
            >
              购买资源包
            </Link>
            ，以免影响数据上报、告警通知等服务。
          </span>
        ) : null}
        {status === ProductExceedStatus.WILL_EXPIRE && ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            尊敬的用户，当前产品套餐即将过期。
          </span>
        ) : null}
        {status === ProductExceedStatus.WILL_EXCEED && ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            尊敬的用户，当前产品资源包剩余上报量少于{hasMauPackage ? '最近两个月' : '最近15天'}用量。
          </span>
        ) : null}

        {status === ProductExceedStatus.HAS_ALREADY_EXCEED && !ENV.isGlobal ? (
          <span className={styles.txt}>
            <RedWarmingFill />
            尊敬的用户，当前已经欠费（{formatNum((exceedQuota || 0) as number)}
            条上报量未正常抵扣），平台已经停止数据上报及告警等服务，请及时
            <Link
              to={{ pathname: '/resource-pack', search: linkParams.toString() }}
              target="_blank"
            >
              购买资源包
            </Link>
            ，恢复相关服务。
          </span>
        ) : null}
        {status === ProductExceedStatus.HAS_ALREADY_EXCEED && ENV.isGlobal ? (
          <span className={styles.txt}>
            <RedWarmingFill />
            尊敬的用户，当前已经欠费（{formatNum((exceedQuota || 0) as number)}
            条上报量未正常抵扣），平台已经停止数据上报及告警等服务。
          </span>
        ) : null}
        <CloseOutlined
          onClick={close}
          title={'关闭'}
          className={styles.closeIcon}
        />
      </div>
    );
  }
  if (showOverdueDataBanner && !liteModeRoutes.includes(location.pathname)) {
    return <Alert
    message={<span dangerouslySetInnerHTML={{ __html: processedMessage }} />}
    type="success"
    showIcon
    className={styles.overdueDataRecoveryReminder}
  /> ;
  }

  // 体验产品过期提醒
  if (productType === ProductType.FREE_PRODUCT
    && product
    && product.freeStatus === FreeStatus.WillExpire
    && !liteModeRoutes.includes(location.pathname)
  ) {
    const expireAtFormat = moment(product.expireDate).format('YYYY年MM月DD日');

    setShowBanner(true);
    return (
      <div
        className={classnames({
          [styles.arrearsReminder]: true,
          [styles.willExceed]: true,
        })}
      >
          <span className={styles.txt}>
            <WarmingFillMax />
            当前产品为体验产品，有效期至{expireAtFormat}。到期后，您将无法在专业版中查看该产品数据。为避免影响您的正常使用，请您及时
            <Link
              to={{ pathname: '/resource-pack', search: linkParams.toString() }}
              target="_blank"
            >升级为付费产品。</Link>
            <CloseOutlined
              onClick={close}
              title={'关闭'}
              className={styles.closeIcon}
            />
          </span>
      </div>
    );
  }

  return (
    <Banner
      showComplianceBanner={showComplianceBanner}// 控制合规banner
      setShowComplianceBanner={setShowComplianceBanner}
      showBanner={showBanner}
      setShowBanner={setShowBanner}
      isShowComplianceModal={isShowComplianceModal}// 控制合规弹窗
      setIsShowComplianceModal={setIsShowComplianceModal}
    />
  );
});
